// CHANGE: Removed all debug console statements for production
// Site management interface for Admins
// Allows creating, viewing, editing, and archiving sites
// Updated with modern dashboard design and improved styling
// CHANGE: Updated to use DropdownMenu component with kebab menu for consistent UX with UserManagement
// CHANGE: Migrated all CRUD operations to use Edge Functions for better security and consistency
// CHANGE: Harmonized design with UnifiedView: reduced title size, improved spacing, modern shadows
// CHANGE: Added sidebar toggle button for collapsible sidebar feature
// CHANGE: Added search bar for better site discovery (client-side filtering)
// CHANGE: Removed filter dropdowns, keeping only search functionality
// UPDATED: Migrated to centralized toast system for all notifications
// UPDATED: Mobile header now has white background with bottom border separator matching bottom nav styling
// CHANGE: Added read-only mode for director role - can view all sites but cannot create/modify/archive
// CHANGE: Harmonized with UserManagement page - simplified title to "Sites" with counter, moved "Archivés" button to header, removed white search wrapper and separate counter block
// CHANGE: Added hover-to-show behavior for kebab menu - menu now appears only on row hover, matching UserManagement UX
// CHANGE: Added mobile responsive card view with ResponsiveDataView and ResponsiveDataCard
// CHANGE: Added SiteDetailModal for viewing site details in read-only mode on mobile
// CHANGE: Added ReadOnlyBanner to inform users they are in consultation mode on mobile
// UPDATED: Implemented server-side pagination with infinite scroll for better performance
// UPDATED: Added skeleton loading states for improved perceived performance
// UX: Added progress indicator showing "X chargés sur Y" for infinite scroll
// REFACTOR: Moved direct supabase.from() user_sites query to getUserSiteCounts() service function
// UPDATED: Replaced SearchBar with ConsolidatedControlBar to show totalCount from server for consistency across all management pages

import { useState, useEffect, useMemo } from 'react';
import { Plus, Pencil, Archive, ArchiveRestore, ArrowUpDown, ArrowUp, ArrowDown, ArrowLeft, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { usePaginatedData } from '../../hooks/usePaginatedData';
import type { Site } from '../../lib/supabase';
import { createSite, updateSite, archiveSite, restoreSite, getUserSiteCounts as fetchUserSiteCounts } from '../../lib/sites';
import { SiteFormModal } from './SiteFormModal';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { DropdownMenu } from '../ui/DropdownMenu';
import type { DropdownMenuItem } from '../ui/DropdownMenu';
import { ConsolidatedControlBar } from '../ui/ConsolidatedControlBar';
import { MobileSearchFilters } from '../ui/MobileSearchFilters';
import { ResponsiveDataView } from '../ui/ResponsiveDataView';
import { ResponsiveDataCard } from '../ui/ResponsiveDataCard';
import { ReadOnlyBanner } from '../ui/ReadOnlyBanner';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { BouncingDotsLoader } from '../ui/BouncingDotsLoader';

type SortField = 'name' | 'created_at' | 'updated_at';
type SortOrder = 'asc' | 'desc';

interface SiteWithUserCount extends Site {
  userCount?: number;
}

export function SiteManagement() {
  const { user: currentUser } = useAuth();
  const { isMobile, isDesktop } = useResponsiveLayout();
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const { showToast } = useToast();

  // Check if current user is director or on mobile (read-only mode)
  const isReadOnly = currentUser?.role === 'director' || isMobile;
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [archivingSite, setArchivingSite] = useState<Site | null>(null);
  const [restoringSite, setRestoringSite] = useState<Site | null>(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [userCounts, setUserCounts] = useState<Map<string, number>>(new Map());

  // Build filters object for server-side filtering
  const filters = useMemo(() => {
    return {
      is_archived: showArchived,
    };
  }, [showArchived]);

  // Use paginated data hook for sites
  const {
    items: sites,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    refresh,
    totalCount,
  } = usePaginatedData<Site>({
    supabase,
    table: 'sites',
    pageSize: 20,
    orderBy: { column: sortField, ascending: sortOrder === 'asc' },
    filters,
    searchColumn: 'name',
    searchTerm,
  });

  // Load user counts separately
  useEffect(() => {
    loadUserCounts();
  }, []);

  const loadUserCounts = async () => {
    try {
      const result = await fetchUserSiteCounts();
      if (result.success && result.data) {
        setUserCounts(result.data);
      }
    } catch (err) {
    }
  };

  // Add user counts to sites
  const sitesWithCounts: SiteWithUserCount[] = useMemo(() => {
    return sites.map(site => ({
      ...site,
      userCount: userCounts.get(site.id) || 0
    }));
  }, [sites, userCounts]);

  const handleCreateSite = async (name: string) => {
    const result = await createSite(name);

    if (result.success) {
      setShowModal(false);
      showToast('Site créé avec succès', 'success');
      refresh();
      loadUserCounts();
    } else {
      showToast(result.error || 'Erreur lors de la création du site', 'error');
    }
  };

  const handleUpdateSite = async (id: string, name: string) => {
    const result = await updateSite(id, name);

    if (result.success) {
      setEditingSite(null);
      showToast('Site modifié avec succès', 'success');
      refresh();
    } else {
      showToast(result.error || 'Erreur lors de la modification du site', 'error');
    }
  };

  const handleArchiveSite = async (site: Site) => {
    setArchivingSite(site);
  };

  const performArchive = async () => {
    if (!archivingSite) return;

    const result = await archiveSite(archivingSite.id);

    if (result.success) {
      setArchivingSite(null);
      showToast('Site archivé avec succès', 'success');
      refresh();
      loadUserCounts();
    } else {
      showToast(result.error || 'Erreur lors de l\'archivage du site', 'error');
      setArchivingSite(null);
    }
  };

  const handleRestoreSite = async (site: Site) => {
    setRestoringSite(site);
  };

  const performRestore = async () => {
    if (!restoringSite) return;

    const result = await restoreSite(restoringSite.id);

    if (result.success) {
      setRestoringSite(null);
      showToast('Site restauré avec succès', 'success');
      refresh();
      loadUserCounts();
    } else {
      showToast(result.error || 'Erreur lors de la restauration du site', 'error');
      setRestoringSite(null);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown size={16} className="text-gray-400" />;
    }
    return sortOrder === 'asc'
      ? <ArrowUp size={16} className="text-primary-600" />
      : <ArrowDown size={16} className="text-primary-600" />;
  };

  const getDropdownItems = (site: Site): DropdownMenuItem[] => {
    // Read-only mode for directors - no actions available
    if (isReadOnly) {
      return [];
    }

    if (showArchived) {
      return [
        {
          label: 'Restaurer',
          icon: <ArchiveRestore size={16} />,
          onClick: () => handleRestoreSite(site),
        },
      ];
    }

    return [
      {
        label: 'Renommer',
        icon: <Pencil size={16} />,
        onClick: () => setEditingSite(site),
      },
      {
        label: 'Archiver',
        icon: <Archive size={16} />,
        onClick: () => handleArchiveSite(site),
        variant: 'danger' as const,
      },
    ];
  };

  const hasActiveSearch = searchTerm.length > 0;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const renderSiteCard = (site: SiteWithUserCount) => {
    return (
      <ResponsiveDataCard
        primaryLabel={site.name}
        secondaryLabel={formatDate(site.created_at)}
        metadata={[
          {
            icon: <Users size={16} className="text-gray-400" />,
            value: `${site.userCount || 0} utilisateur${(site.userCount || 0) > 1 ? 's' : ''}`
          }
        ]}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Read-only banner for mobile */}
      {isMobile && <ReadOnlyBanner />}

      {/* Header */}
      {showArchived ? (
        <div className={isMobile ? 'bg-white border-b border-gray-200 -mx-4 px-4 pb-4' : ''}>
          <button
            onClick={() => setShowArchived(false)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors mb-4"
          >
            <ArrowLeft size={16} />
            <span>Retour</span>
          </button>
          <PageHeader title="Sites archivés" />
        </div>
      ) : (
        <PageHeader title="Sites" isMobile={isMobile}>
          {!isReadOnly && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowArchived(true)}
                className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-medium px-4 py-2.5 rounded-lg transition-all duration-200 border border-gray-200 shadow-sm hover:shadow-md"
              >
                <Archive size={18} />
                <span>Archivés</span>
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Plus size={18} />
                <span>Créer</span>
              </button>
            </div>
          )}
        </PageHeader>
      )}

      {/* Search Bar */}
      {!showArchived && (
        <>
          {isDesktop && (
            <ConsolidatedControlBar
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Rechercher un site..."
              itemCount={totalCount}
              itemLabel="site"
              showSort={false}
            />
          )}
          {isMobile && (
            <MobileSearchFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Rechercher un site..."
              showFilterButton={false}
            />
          )}
        </>
      )}

      {/* Sites table/cards */}
      {sitesWithCounts.length === 0 && !isLoading ? (
        <EmptyState
          message={showArchived ? 'Aucun site archivé' : hasActiveSearch ? 'Aucun site ne correspond à votre recherche' : 'Aucun site trouvé'}
          actionLabel={hasActiveSearch && !showArchived ? 'Effacer la recherche' : undefined}
          onAction={hasActiveSearch && !showArchived ? () => setSearchTerm('') : undefined}
        />
      ) : (
        <ResponsiveDataView
          items={sitesWithCounts}
          isDesktop={isDesktop}
          cardRenderer={renderSiteCard}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          tableSkeletonComponent={
            <div className="flex items-center justify-center min-h-[400px]">
              <BouncingDotsLoader size="lg" />
            </div>
          }
          tableComponent={
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <button
                          onClick={() => handleSort('name')}
                          className="flex items-center gap-2 hover:text-gray-900 transition-colors"
                        >
                          <span>NOM DU SITE</span>
                          {getSortIcon('name')}
                        </button>
                      </th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        NOMBRE D'UTILISATEURS
                      </th>
                      <th className="hidden md:table-cell text-right px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sitesWithCounts.map((site) => (
                      <tr key={site.id} className="hover:bg-gray-50 transition-colors group">
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                          {site.name}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {site.userCount || 0}
                        </td>
                        <td className="hidden md:table-cell px-6 py-4 text-sm text-right">
                          <div className="flex items-center justify-end">
                            <DropdownMenu items={getDropdownItems(site)} align="right" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          }
        />
      )}

      {showModal && (
        <SiteFormModal
          onClose={() => setShowModal(false)}
          onSubmit={handleCreateSite}
        />
      )}

      {editingSite && (
        <SiteFormModal
          site={editingSite}
          onClose={() => setEditingSite(null)}
          onSubmit={(name) => handleUpdateSite(editingSite.id, name)}
        />
      )}

      {/* Archive Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!archivingSite}
        title="Confirmer l'archivage"
        message={`Êtes-vous sûr de vouloir archiver le site "${archivingSite?.name}" ? Il sera déplacé dans les sites archivés et ne sera plus accessible aux utilisateurs.`}
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        onConfirm={performArchive}
        onCancel={() => setArchivingSite(null)}
        variant="warning"
      />

      {/* Restore Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={!!restoringSite}
        title="Confirmer la restauration"
        message={`Êtes-vous sûr de vouloir restaurer le site "${restoringSite?.name}" ? Il sera réactivé et à nouveau accessible aux utilisateurs.`}
        confirmLabel="Confirmer"
        cancelLabel="Annuler"
        onConfirm={performRestore}
        onCancel={() => setRestoringSite(null)}
        variant="info"
      />
    </div>
  );
}
